import { useOnClickOutside } from "@shared/hooks/useOnClickOutside";
import theme from "@shared/utils/themeColors";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PATHS } from "../../../config/paths";
import { useAuth } from "../../../features/auth/hooks/useAuth";
import { useProfile } from "../../hooks/useProfile";

interface DropdownProfileProps {
  align?: "left" | "right";
}

export function DropdownProfile({ align = "right" }: DropdownProfileProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { logout } = useAuth();
  const { user, getProfileThumbnail } = useProfile();
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Close on click outside
  useOnClickOutside(dropdown, () => setDropdownOpen(false));

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  if (!user) return null;

  const { profile, email } = user;

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className="inline-flex justify-center items-center group"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <img
          className="w-8 h-8 rounded-full"
          src={getProfileThumbnail(profile, email)}
          alt="User"
        />
        <div className="flex items-center truncate">
          <span className="truncate ml-2 text-sm font-medium dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-gray-200">
            {profile?.name || email?.split("@")[0]}
          </span>
          <svg
            className="w-3 h-3 shrink-0 ml-1 fill-current text-gray-400"
            viewBox="0 0 12 12"
          >
            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
          </svg>
        </div>
      </button>

      <div
        ref={dropdown}
        className={`origin-top-right z-10 absolute top-full ${
          align === "right" ? "right-0" : "left-0"
        } min-w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 py-1.5 rounded shadow-lg overflow-hidden mt-1 ${
          dropdownOpen ? "block" : "hidden"
        }`}
      >
        <div className="pt-0.5 pb-2 px-3 mb-1 border-b border-gray-200 dark:border-gray-700">
          <Link
            className={`font-medium text-sm ${theme.text.link} flex flex-col py-1 px-3`}
            to={PATHS.COMMUNITY.MY_PROFILE}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <div className="font-medium text-gray-800 dark:text-gray-200">
              {profile?.name}
            </div>
            <div className={`text-xs ${theme.text.muted}`}>{email}</div>
          </Link>
        </div>
        <ul>
          <li>
            <Link
              className={`font-medium text-sm ${theme.text.link} flex flex-col py-1 px-3`}
              to={PATHS.COMMUNITY.MY_PROFILE}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              My Profile
            </Link>
          </li>
          <li>
            <Link
              className={`font-medium text-sm ${theme.text.link} flex flex-col py-1 px-3`}
              to="/settings/account"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              Settings
            </Link>
          </li>
          <li>
            <button
              onClick={logout}
              className={`font-medium text-sm ${theme.text.link} flex flex-col py-1 px-3`}
            >
              Sign Out
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
}
